/**
 * Seat an assembled asset on the origin the conventions promise.
 *
 * `docs/asset-conventions.md` §2: *origin at the ground contact point, centred in
 * X/Z*, so that placing an asset is `position.set(x, 0, z)` with no fudge
 * factors. `tests/e2e/asset.spec.ts` enforces it on every shippable asset, with
 * 1 mm of tolerance on the ground and 20–30 mm on the centring.
 *
 * **The rule is easy to satisfy on paper and easy to miss in code**, because the
 * natural way to author a piece of kit is to put the interesting face at z = 0
 * and build forward from it — which is exactly what a hose reel bolted to a wall
 * wants to do, and it lands the finished object 0.17 m off-centre. Five assets in
 * one batch missed it the same way, which is what this helper is for: measure
 * what was actually built and move it, rather than keeping a running total of
 * offsets in the author's head.
 *
 * `finisher.ts` and `dog.ts` do this inline, and deliberately still do: both are
 * characters assembled inside a nested pose rig, so they have a node of their own
 * to offset and no need to walk a child list. This is the flat-kit version.
 */

import * as THREE from 'three';

export interface SeatOptions {
  /**
   * Whether to drop the asset onto y = 0.
   *
   * Default true. Pass `false` only for something that is deliberately buried —
   * `drain-channel.ts` keeps its lowest geometry a few millimetres above the
   * datum rather than below it, because an asset that sinks reads as a z-fighting
   * bug in every turnaround even when it is correct in a floor.
   */
  ground?: boolean;
}

/**
 * Translate every direct child so the assembly is centred in X/Z and, by
 * default, sitting on y = 0.
 *
 * Call it as the last statement of `build()`, after everything has been added.
 * Children are moved rather than the group itself, because the group's own
 * transform belongs to whoever places the asset — the preview harness and the
 * room both set `position` on it, and an offset stored there would be silently
 * overwritten by the first placement.
 */
export function seatOnOrigin(group: THREE.Group, options: SeatOptions = {}): void {
  group.updateMatrixWorld(true);
  const bounds = new THREE.Box3().setFromObject(group);
  if (bounds.isEmpty()) return;

  const dx = -(bounds.min.x + bounds.max.x) / 2;
  const dz = -(bounds.min.z + bounds.max.z) / 2;
  const dy = options.ground === false ? 0 : -bounds.min.y;

  for (const child of group.children) {
    // `position` is in parent space, so this is safe for children carrying their
    // own rotation — which most of this kit does.
    child.position.set(child.position.x + dx, child.position.y + dy, child.position.z + dz);
  }
}
